// The browser's side of the Jev proxy: one POST to `/api/jev`, one row in the AI audit, and never an
// exception. The counterpart of `src/ai/client.ts` for a model that answers with typed judgments
// instead of text.
//
// Three things it adds over a bare fetch.
//
// **A per-picture request cache.** The two picture-level questions are asked over one state, and the
// key is the state itself (`stateKey`), not the tick: a tick that changed nothing about the common
// operating picture costs nothing, and two components asking at the same moment share one in-flight
// promise rather than racing to spend the same money twice.
//
// **An audit row per call**, with the columns an estimate needs rather than the ones a text answer
// needs: provider, confidence, calibrated. A Jev call that times out and is answered by the heuristic
// is recorded as `provider: 'heuristic'` with the error attached — on this app's fallback design that
// distinction is invisible in the interface otherwise, which is exactly the failure the audit exists
// to prevent (see `src/ai/audit.ts`).
//
// **The last latency**, kept here so the instructor toggle's status line can show what the last call
// actually cost without subscribing to the whole audit.

import * as audit from './audit';
import type { AuditPurpose } from './audit';
import type { EstimatorQuestion } from './calibration';
import { JEV_TIMEOUT_MS, type JevAnswer, type JevProxyResult, type JevQuestion } from './jevProxyCore';

/** Maps our question ids onto the audit purposes, which are three literals so the label record stays
 * exhaustive (see the comment on `AuditPurpose`). */
const PURPOSE_FOR: Record<EstimatorQuestion, AuditPurpose> = {
  mainEffortThird: 'jev:mainEffortThird',
  contactRole: 'jev:contactRole',
  reserveCommitSoon: 'jev:reserveCommitSoon',
};

export function auditPurposeFor(question: EstimatorQuestion): AuditPurpose {
  return PURPOSE_FOR[question];
}

export interface JevCallResult {
  ok: boolean;
  answers: Record<string, JevAnswer>;
  error?: string;
  latencyMs: number;
  model?: string;
}

/** The last thing the proxy did, for the toggle's status line. */
export interface JevHealth {
  /** Milliseconds of the most recent call, whatever its outcome. */
  lastLatencyMs: number | null;
  /** Whether the most recent call produced an answer. */
  lastOk: boolean | null;
  /** The most recent failure's message, cleared by the next success. */
  lastError: string | null;
  calls: number;
  failures: number;
}

let health: JevHealth = { lastLatencyMs: null, lastOk: null, lastError: null, calls: 0, failures: 0 };
const healthListeners = new Set<() => void>();

export function jevHealth(): JevHealth {
  return health;
}

export function subscribeJevHealth(fn: () => void): () => void {
  healthListeners.add(fn);
  return () => { healthListeners.delete(fn); };
}

function publishHealth(next: JevHealth): void {
  health = next;
  for (const fn of healthListeners) fn();
}

/** Test seam. */
export function resetJevHealth(): void {
  publishHealth({ lastLatencyMs: null, lastOk: null, lastError: null, calls: 0, failures: 0 });
}

// ---- the in-flight / recent cache ---------------------------------------------------------------------

/** How long an answer for an unchanged picture stays good. A fight moves in sim minutes; this is a
 * guard against two components asking within the same frame, not a content cache. */
const CACHE_TTL_MS = 30_000;
const CACHE_LIMIT = 32;

interface CacheEntry { at: number; promise: Promise<JevCallResult> }

const cache = new Map<string, CacheEntry>();

function cacheGet(key: string): Promise<JevCallResult> | undefined {
  const hit = cache.get(key);
  if (!hit) return undefined;
  if (Date.now() - hit.at > CACHE_TTL_MS) { cache.delete(key); return undefined; }
  return hit.promise;
}

function cachePut(key: string, promise: Promise<JevCallResult>): void {
  cache.set(key, { at: Date.now(), promise });
  if (cache.size > CACHE_LIMIT) {
    const oldest = cache.keys().next();
    if (!oldest.done) cache.delete(oldest.value);
  }
}

/** Test seam, and what the live stage should call between fights. */
export function clearJevCache(): void {
  cache.clear();
}

// ---- the call ------------------------------------------------------------------------------------------

export interface JevCallOptions {
  /** Which judgment this call is for, for the audit row. */
  question: EstimatorQuestion;
  /** What names this particular call — "main effort + reserve, H+47" or "CONTACT 12". */
  ref?: string;
  /** Cache key; defaults to the serialised state plus the question ids. */
  cacheKey?: string;
  /** Injectable for tests. Defaults to the global `fetch`. */
  fetchImpl?: typeof fetch;
  /**
   * The audit row this call should be attached to, when the caller has already opened one.
   *
   * The estimator opens the row itself, because the row has to record the *provider that finally
   * answered* — which may be the heuristic, after this call times out — and that is not known here.
   */
  auditId?: number;
}

/**
 * One call to `/api/jev`. Never throws.
 *
 * The abort here is belt and braces: `proxyJev` on the server already gives up at `JEV_TIMEOUT_MS`, but
 * a dev server that is not running at all leaves this fetch hanging until the browser gives up, and
 * "the card sat there" is not an acceptable outcome for a card whose whole promise is 70-500 ms.
 */
export async function callJev(
  state: unknown,
  questions: Record<string, JevQuestion>,
  opts: JevCallOptions,
): Promise<JevCallResult> {
  const key = opts.cacheKey ?? `${Object.keys(questions).sort().join('|')}\u0000${JSON.stringify(state)}`;
  const hit = cacheGet(key);
  if (hit) return hit;

  const promise = performCall(state, questions, opts);
  cachePut(key, promise);
  return promise;
}

async function performCall(
  state: unknown,
  questions: Record<string, JevQuestion>,
  opts: JevCallOptions,
): Promise<JevCallResult> {
  const doFetch = opts.fetchImpl ?? fetch;
  const started = Date.now();
  const controller = new AbortController();
  // A little past the server's own budget, so a server that answers at the edge of it is not cut off
  // by its own client and reported as a network failure.
  const timer = setTimeout(() => controller.abort(), JEV_TIMEOUT_MS + 500);

  const settle = (result: JevCallResult): JevCallResult => {
    publishHealth({
      lastLatencyMs: result.latencyMs,
      lastOk: result.ok,
      lastError: result.ok ? null : result.error ?? 'unknown error',
      calls: health.calls + 1,
      failures: health.failures + (result.ok ? 0 : 1),
    });
    return result;
  };

  try {
    const res = await doFetch('/api/jev', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ state, questions }),
      signal: controller.signal,
    });

    const data = (await res.json().catch(() => null)) as JevProxyResult | null;
    const latencyMs = data && typeof data.latencyMs === 'number' ? data.latencyMs : Date.now() - started;

    if (!data || data.ok !== true) {
      const error = data && data.ok === false ? data.error : `jev proxy returned status ${res.status}`;
      return settle({ ok: false, answers: {}, error, latencyMs });
    }
    return settle({ ok: true, answers: data.answers, latencyMs, model: data.model });
  } catch (err) {
    const aborted = err instanceof Error && err.name === 'AbortError';
    return settle({
      ok: false,
      answers: {},
      error: aborted ? `jev: client gave up after ${JEV_TIMEOUT_MS + 500} ms` : err instanceof Error ? err.message : String(err),
      latencyMs: Date.now() - started,
    });
  } finally {
    clearTimeout(timer);
  }
}

/** Opens an audit row for an estimate. Closed by `closeEstimateRow` once a provider has answered. */
export function openEstimateRow(question: EstimatorQuestion, promptChars: number, ref?: string): number {
  return audit.record({
    purpose: auditPurposeFor(question),
    model: 'jev-latest',
    promptChars,
    ref,
  });
}

/** Closes it with what actually happened, including a fallback to the heuristic. */
export function closeEstimateRow(
  id: number,
  patch: {
    provider: 'heuristic' | 'jev';
    latencyMs: number;
    confidence: number;
    calibrated: boolean;
    error?: string;
  },
): void {
  audit.note(id, {
    latencyMs: Math.round(patch.latencyMs),
    // `source` keeps its existing meaning: the answer came from a model, or from the deterministic
    // path that stands in when it does not. `provider` says which deterministic path.
    source: patch.provider === 'jev' ? 'llm' : 'template',
    provider: patch.provider,
    confidence: patch.confidence,
    calibrated: patch.calibrated,
    error: patch.error,
  });
}
